import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { TrendUp, Warning, ShieldCheck, MagnifyingGlass, Buildings, ImageSquare, Clock, Sparkle } from "@phosphor-icons/react";

const Kpi = ({ label, value, sub, icon: Icon, tone = "default", testid }) => {
  const toneMap = {
    default: "text-[#0A3B2C]",
    high: "text-[#DC2626]",
    medium: "text-[#B45309]",
    low: "text-[#047857]",
  };
  return (
    <Card data-testid={testid} className="p-5 rounded-sm border card-hover">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className={`font-heading font-bold text-3xl mt-2 ${toneMap[tone]}`}>{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        {Icon && <Icon size={20} weight="bold" className="text-[#0A3B2C]/60" />}
      </div>
    </Card>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-sm text-muted-foreground">Carregando...</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Visão geral</div>
          <h1 className="font-heading font-extrabold text-4xl lg:text-5xl">Confidence Brand Monitor</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Painel operacional de monitoramento contínuo. Resultados dependem das fontes, APIs e páginas públicas consultadas.
          </p>
        </div>
        <div className="text-right text-xs">
          <div className="text-muted-foreground uppercase tracking-wider">Última varredura</div>
          <div className="font-mono">{stats.ultima_varredura ? new Date(stats.ultima_varredura).toLocaleString("pt-BR") : "Nenhuma"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-total" label="Total de resultados" value={stats.total_resultados} icon={TrendUp} />
        <Kpi testid="kpi-novos" label="Novos no período" value={stats.novos_resultados} icon={Sparkle} />
        <Kpi testid="kpi-alto" label="Risco alto" value={stats.risco_alto} tone="high" icon={Warning} />
        <Kpi testid="kpi-medio" label="Risco médio" value={stats.risco_medio} tone="medium" icon={ShieldCheck} />
        <Kpi testid="kpi-baixo" label="Risco baixo" value={stats.risco_baixo} tone="low" icon={ShieldCheck} />
        <Kpi testid="kpi-inpi" label="Pedidos no INPI" value={stats.pedidos_inpi} icon={Buildings} />
        <Kpi testid="kpi-social" label="Perfis sociais" value={stats.perfis_sociais} icon={MagnifyingGlass} />
        <Kpi testid="kpi-imagens" label="Imagens semelhantes" value={stats.imagens_semelhantes} icon={ImageSquare} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 rounded-sm border lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">Descobertas por período</h3>
            <Clock size={16} className="text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.chart_by_day}>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} />
                <Line type="monotone" dataKey="count" stroke="#0A3B2C" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-sm border">
          <h3 className="font-heading font-bold text-lg mb-4">Distribuição por risco</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.chart_by_risk} dataKey="value" nameKey="name" outerRadius={80} innerRadius={50}>
                  {stats.chart_by_risk.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-around text-xs mt-2">
            {stats.chart_by_risk.map((e) => (
              <div key={e.name} className="flex items-center gap-1">
                <span className="w-2 h-2" style={{ background: e.color }} />
                <span>{e.name}: {e.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="p-3 border-l-4 border-[#0A3B2C] bg-[#F3F4F6] text-xs">
        <strong>Aviso:</strong> Esta plataforma exibe dados demonstrativos até que as integrações com APIs externas
        (SerpAPI, INPI, Google Vision, redes sociais) sejam conectadas. Nenhum resultado é apresentado como real.
      </div>
    </div>
  );
}
